import React from 'react';
import {
  getBookColor,
  getAccentColor,
  getBookCoverGradient,
  getBookTextColor,
} from '../utils/bookColors';
import VoxSmythIcons from '../VoxSmythIcons';

/**
 * BookMiniCard — compact, category-colored book cover used on mobile
 * surfaces where the full 200×320 BookCard is too large (Emporium grid,
 * Library grid, Recently Added rail).
 *
 * Visual contract:
 *  - 100×140 cover that retains the same colored gradient + accent border
 *    + corner flourishes as the full BookCard, so a romance novel still
 *    reads as mauve, a sci-fi book as slate-teal, etc.
 *  - Optional badges (FREE / progress %) overlaid on the cover.
 *  - Title (1 line clamp) + author (1 line clamp) below the cover.
 *  - The entire card is a tappable button — buttons inside the card are
 *    intentionally omitted (mobile users tap to navigate to the detail
 *    page where the Play / Add to Library actions live).
 */
export const BookMiniCard = ({
  book,
  onClick,
  showFreeBadge = false,
  progress,
  testId,
}) => {
  const bookColor = getBookColor(book);
  const accentColor = getAccentColor(book);
  const coverGradient = getBookCoverGradient(book);
  const textColor = getBookTextColor(book);
  const isChildrens = book && book.genre === "Children's Literature";

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="book-mini-card"
      style={{
        flex: '0 0 auto',
        width: '100px',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        scrollSnapAlign: 'start',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div
        style={{
          width: '100px',
          height: '140px',
          borderRadius: '8px',
          background: coverGradient,
          border: isChildrens ? `2px solid ${accentColor}` : `1.5px solid ${accentColor}`,
          boxShadow: isChildrens
            ? `0 4px 14px rgba(66, 165, 245, 0.25), 0 0 12px rgba(255, 249, 196, 0.4)`
            : '0 4px 14px rgba(44, 58, 76, 0.22)',
          position: 'relative',
          overflow: 'hidden',
          marginBottom: '8px',
        }}
      >
        {/* Corner flourishes — same as the full BookCard so the aesthetic
            stays consistent at this smaller scale. */}
        <span style={{
          position: 'absolute', top: '4px', left: '4px',
          width: '10px', height: '10px',
          borderTop: `1.5px solid ${accentColor}`,
          borderLeft: `1.5px solid ${accentColor}`,
          opacity: 0.65,
        }} />
        <span style={{
          position: 'absolute', top: '4px', right: '4px',
          width: '10px', height: '10px',
          borderTop: `1.5px solid ${accentColor}`,
          borderRight: `1.5px solid ${accentColor}`,
          opacity: 0.65,
        }} />
        <span style={{
          position: 'absolute', bottom: '4px', left: '4px',
          width: '10px', height: '10px',
          borderBottom: `1.5px solid ${accentColor}`,
          borderLeft: `1.5px solid ${accentColor}`,
          opacity: 0.65,
        }} />
        <span style={{
          position: 'absolute', bottom: '4px', right: '4px',
          width: '10px', height: '10px',
          borderBottom: `1.5px solid ${accentColor}`,
          borderRight: `1.5px solid ${accentColor}`,
          opacity: 0.65,
        }} />

        {/* Title overlay — centered on the cover, scaled down for the
            mini size but keeps the typographic personality. */}
        <div style={{
          position: 'absolute',
          inset: 0,
          padding: '14px 8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
        }}>
          <h3 style={{
            color: textColor,
            fontFamily: 'DM Serif Display, Georgia, serif',
            fontSize: '11px',
            lineHeight: 1.15,
            textAlign: 'center',
            margin: 0,
            textShadow: isChildrens ? 'none' : '0 1px 3px rgba(0,0,0,0.45)',
            fontWeight: 600,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}>
            {book.title}
          </h3>
          <div style={{
            width: '40%',
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
            opacity: 0.8,
          }} />
          <p style={{
            color: textColor,
            fontFamily: 'Inter, sans-serif',
            fontSize: '8.5px',
            textAlign: 'center',
            margin: 0,
            opacity: 0.92,
            textShadow: isChildrens ? 'none' : '0 1px 2px rgba(0,0,0,0.45)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {book.author}
          </p>
        </div>

        {showFreeBadge && (
          <span style={{
            position: 'absolute',
            top: '5px',
            left: '5px',
            background: 'linear-gradient(135deg, #10B981 0%, #047857 100%)',
            color: 'white',
            fontFamily: 'Inter, sans-serif',
            fontSize: '7.5px',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '2px 5px',
            borderRadius: '6px',
            boxShadow: '0 1px 4px rgba(16, 185, 129, 0.4)',
            zIndex: 2,
          }}>
            Free
          </span>
        )}

        {typeof progress === 'number' && progress > 0 && (
          <span style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
            color: 'white',
            fontFamily: 'Inter, sans-serif',
            fontSize: '7.5px',
            fontWeight: 800,
            padding: '2px 5px',
            borderRadius: '6px',
            zIndex: 2,
          }}>
            {Math.round(progress * 100)}%
          </span>
        )}
      </div>

      {/* External title row (some surfaces hide this — controlled by
          the consumer wrapping the card). Kept compact and ellipsized. */}
      <div
        className="book-mini-card__title"
        style={{
          fontFamily: 'DM Serif Display, Georgia, serif',
          fontSize: '12px',
          lineHeight: 1.25,
          color: '#2C3A4C',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          marginBottom: '2px',
        }}
      >
        {book.title}
      </div>
      <div
        className="book-mini-card__author"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '10px',
          color: '#64748B',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {book.author}
      </div>
    </button>
  );
};

/**
 * BookMiniCover — even smaller cover-only chip (52×72) used in row-style
 * components like ContinueListeningRail. No title/author text.
 */
export const BookMiniCover = ({ book, size = 'sm' }) => {
  const accentColor = getAccentColor(book);
  const coverGradient = getBookCoverGradient(book);
  const isChildrens = book && book.genre === "Children's Literature";

  const dims = size === 'lg'
    ? { w: 70, h: 96, radius: 8 }
    : { w: 52, h: 72, radius: 6 };

  return (
    <div
      data-testid="book-mini-cover"
      style={{
        width: `${dims.w}px`,
        height: `${dims.h}px`,
        borderRadius: `${dims.radius}px`,
        flexShrink: 0,
        background: coverGradient,
        border: `1.5px solid ${accentColor}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: isChildrens
          ? `0 2px 8px rgba(66, 165, 245, 0.25)`
          : '0 2px 8px rgba(44, 58, 76, 0.2)',
      }}
    >
      {book && book.cover && !String(book.cover).includes('/api/placeholder') ? (
        <img
          src={book.cover}
          alt={book.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <VoxSmythIcons.BookOpen size={Math.round(dims.w * 0.4)} color={accentColor} />
      )}
    </div>
  );
};

export default BookMiniCard;
